import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { BadgeCheck, Check, X, ShieldCheck, Loader2, AlertTriangle, MapPin, Calendar } from "lucide-react";

// Public, unauthenticated Crash Ready Verified status page (client-shared).
export default function VerifiedBadgePage() {
  const { token } = useParams();
  const [data, setData] = useState(undefined);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/verified/${token}`)
      .then((r) => setData(r.data)).catch(() => setData(null));
  }, [token]);

  if (data === undefined) return <div className="min-h-screen flex items-center justify-center bg-slate-100"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>;

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 p-4">
        <div className="bg-white rounded-2xl shadow-sm max-w-md w-full p-8 text-center" data-testid="verified-share-error">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <h1 className="font-bold text-lg text-slate-800">Link unavailable</h1>
          <p className="text-sm text-slate-500 mt-2">This verification link is invalid or has been revoked.</p>
        </div>
      </div>
    );
  }

  const crv = data.crash_ready_verified || {};
  const done = (crv.components || []).filter((c) => c.done).length;

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 p-4">
      <div className="bg-white rounded-3xl shadow-sm max-w-lg w-full overflow-hidden" data-testid="verified-share-page">
        <div className={`p-6 flex items-center gap-3 ${crv.achieved ? "bg-emerald-600" : "bg-navy"} text-white`}>
          <BadgeCheck className="w-9 h-9" />
          <div>
            <div className="font-heading font-extrabold tracking-wide text-lg" data-testid="verified-share-status">
              {crv.achieved ? "CRASH READY VERIFIED" : `Verification in progress — ${done}/${(crv.components || []).length}`}
            </div>
            <div className="text-xs opacity-80">Independent verification checklist · Crash Ready</div>
          </div>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-2 gap-2 text-xs text-slate-500 mb-4">
            <div><span className="block text-slate-400">Case ID</span><span className="font-mono text-navy">{data.case_number || "—"}</span></div>
            <div><span className="block text-slate-400">Verification tier</span>{data.trust_class || "—"}</div>
            <div className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {data.accident_date ? new Date(data.accident_date).toLocaleDateString() : "—"}</div>
            <div className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {[data.city, data.state].filter(Boolean).join(", ") || "—"}</div>
          </div>
          <div className="grid sm:grid-cols-2 gap-1.5" data-testid="verified-share-components">
            {(crv.components || []).map((c) => (
              <div key={c.key} className="flex items-center gap-2 text-sm">
                {c.done ? <Check className="w-4 h-4 text-emerald-600 shrink-0" /> : <X className="w-4 h-4 text-slate-300 shrink-0" />}
                <span className={c.done ? "text-navy" : "text-slate-400"}>{c.label}</span>
              </div>
            ))}
          </div>
          <div className="mt-5 flex items-start gap-2 text-[10px] text-slate-400">
            <ShieldCheck className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            {crv.disclaimer || "This status documents completed verification steps. It does not guarantee liability, damages, insurance coverage or legal merit."}
          </div>
        </div>
      </div>
    </div>
  );
}
